import { DateTime } from "luxon";
import { capitalise } from "../../utils/string";
import { ViewSchema } from "./types";

const DATE_FORMAT = "yyyy-MM-dd";

const serialiseDate = (value: Date | null): string | null =>
  value ? DateTime.fromJSDate(value).toFormat(DATE_FORMAT) : null;

const deserialiseDate = (raw: string): Date | undefined => {
  const parsed = DateTime.fromFormat(raw, DATE_FORMAT);
  return parsed.isValid ? parsed.toJSDate() : undefined;
};

interface OptionEntry {
  value: number | string;
  label: string;
}

const findLabel = (options: unknown[], value: unknown): string | undefined =>
  (options as OptionEntry[]).find((option) => option.value === value)?.label;

const isKnownOption = (options: unknown[], value: unknown): boolean =>
  (options as OptionEntry[]).some((option) => option.value === value);

export const monitoringView: ViewSchema = {
  dashboard: "monitoring",
  fields: {
    // New: a genuine addition to the page's state, correcting the
    // pre-existing inconsistency where MonitoringDateFilters renders a
    // Status selector but nothing defaults or resets it (tech spec 4.5).
    status: {
      param: "status",
      default: "all",
      serialise: (value: string | number) => (value === "all" ? null : String(value)),
      deserialise: (raw) => {
        if (raw === "all") return "all";
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : undefined;
      },
      optionsKey: "statuses",
      validate: (value, options) => value === "all" || isKnownOption(options, value),
      chipLabel: (value, options) =>
        `Status: ${capitalise(findLabel(options, value) ?? String(value))}`,
      inFilterPanel: true,
    },
    createdOnFrom: {
      param: "from",
      default: null,
      serialise: serialiseDate,
      deserialise: deserialiseDate,
      chipLabel: (value: Date) =>
        `Created from: ${DateTime.fromJSDate(value).toFormat("d LLL yyyy")}`,
      inFilterPanel: true,
    },
    createdOnTo: {
      param: "to",
      default: null,
      serialise: serialiseDate,
      deserialise: deserialiseDate,
      chipLabel: (value: Date) =>
        `Created to: ${DateTime.fromJSDate(value).toFormat("d LLL yyyy")}`,
      inFilterPanel: true,
    },
    lastActivityFrom: {
      param: "afrom",
      default: null,
      serialise: serialiseDate,
      deserialise: deserialiseDate,
      chipLabel: (value: Date) =>
        `Last activity from: ${DateTime.fromJSDate(value).toFormat("d LLL yyyy")}`,
      inFilterPanel: true,
    },
    lastActivityTo: {
      param: "ato",
      default: null,
      serialise: serialiseDate,
      deserialise: deserialiseDate,
      chipLabel: (value: Date) =>
        `Last activity to: ${DateTime.fromJSDate(value).toFormat("d LLL yyyy")}`,
      inFilterPanel: true,
    },
    search: {
      param: "q",
      default: "",
      serialise: (value: string) => (value === "" ? null : value),
      deserialise: (raw) => raw,
      session: true,
    },
    tab: {
      param: "tab",
      default: 0,
      serialise: (value: number) => (value === 0 ? null : String(value)),
      deserialise: (raw) => {
        const parsed = Number(raw);
        return [0, 1, 2].includes(parsed) ? parsed : undefined;
      },
    },
    sortBy: {
      param: "sort",
      default: "created_on",
      serialise: (value: string) => (value === "created_on" ? null : value),
      deserialise: (raw) => raw,
    },
    sortDesc: {
      param: "dir",
      default: true,
      serialise: (value: boolean) => (value ? null : "asc"),
      deserialise: (raw) => raw !== "asc",
    },
    pageSize: {
      param: "size",
      default: 10,
      serialise: (value: number) => (value === 10 ? null : String(value)),
      deserialise: (raw) => {
        const parsed = Number(raw);
        return Number.isInteger(parsed) ? parsed : undefined;
      },
      optionsKey: "pageSizes",
      validate: (value, options) => (options as number[]).includes(value as number),
    },
  },
};
